import type React from 'react';
import { useCallback, useRef, useEffect, useState } from 'react';

import * as SelectPrimitive from '@radix-ui/react-select';
import { useVirtualizer } from '@tanstack/react-virtual';
import cx from 'classnames';

import styles from '../SelectOptions/SelectOptions.module.css';

interface VirtualizedSelectOptionsProps {
  className?: string;
  position?: React.ComponentProps<typeof SelectPrimitive.Content>['position'];
  itemCount: number;
  estimateSize?: number | ((index: number) => number);
  overscan?: number;
  renderItem: (index: number, isFocused: boolean) => React.ReactNode;
  isOpen: boolean;
  selectedIndex?: number;
  onValueChange?: (value: string) => void;
  getItemValue?: (index: number) => string;
}

export default function VirtualizedSelectOptions({
  className,
  position = 'popper',
  itemCount,
  estimateSize = 48,
  overscan = 8,
  isOpen,
  renderItem,
  selectedIndex,
  onValueChange,
  getItemValue,
}: VirtualizedSelectOptionsProps): JSX.Element {
  const scrollElementRef = useRef<HTMLDivElement | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(selectedIndex ?? -1);

  const virtualizer = useVirtualizer({
    count: itemCount,
    getScrollElement: () => scrollElementRef.current,
    estimateSize:
      typeof estimateSize === 'function' ? estimateSize : () => estimateSize,
    overscan,
  });

  const items = virtualizer.getVirtualItems();

  useEffect(() => {
    if (isOpen && selectedIndex !== undefined && selectedIndex >= 0) {
      setFocusedIndex(selectedIndex);
      requestAnimationFrame(() => {
        virtualizer.scrollToIndex(selectedIndex, {
          align: 'center',
        });
      });
    }
  }, [isOpen, selectedIndex, virtualizer]);

  useEffect(() => {
    if (focusedIndex >= 0 && focusedIndex < itemCount) {
      virtualizer.scrollToIndex(focusedIndex, {
        align: 'center',
      });
    }
  }, [focusedIndex, virtualizer, itemCount]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev + 1;
            return next >= itemCount ? 0 : next;
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev - 1;
            return next < 0 ? itemCount - 1 : next;
          });
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (focusedIndex >= 0 && getItemValue && onValueChange) {
            const value = getItemValue(focusedIndex);
            onValueChange(value);
          }
          break;
        case 'Home':
          event.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setFocusedIndex(itemCount - 1);
          break;
      }
    },
    [isOpen, itemCount, focusedIndex, getItemValue, onValueChange]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
    return undefined;
  }, [isOpen, handleKeyDown]);

  const handleViewportRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollElementRef.current = node;

      if (node) {
        requestAnimationFrame(() => {
          virtualizer.measure();
        });
      }
    },
    [virtualizer]
  );

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        className={cx(styles['base'], className)}
        position={position}
      >
        <SelectPrimitive.Viewport
          ref={handleViewportRef}
          style={{
            maxHeight: '198px',
            overflow: 'auto',
            position: 'relative',
          }}
        >
          <div
            style={{
              height: virtualizer.getTotalSize(),
            }}
          >
            {items.map((virtualRow) => {
              const isFocused = virtualRow.index === focusedIndex;
              return (
                <div
                  key={String(virtualRow.key)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  {renderItem(virtualRow.index, isFocused)}
                </div>
              );
            })}
          </div>
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}
